import React from 'react';
import FacilityStore from '../../stores/FacilityStore';
import ViewerStore from '../../stores/ViewerStore';
import { SplitterComponent, PanesDirective, PaneDirective } from '@syncfusion/ej2-react-layouts';
import ReactPointViewer from '../pointcloudviewer/ReactPointViewer';
import ReactPanoViewer from './ReactPanoViewer';
import AssetInformation from '../components/AssetInformation';
import ViewerActions from '../../actions/ViewerActions';
import AssetTagsBar from '../../components/AssetTagsBar';
import ShowPointCloudButton from '../../components/ShowPointCloudButton';
import MeasurementBar from '../../components/MeasurementBar';
import AssemblyLibraryBar from '../../components/AssemblyLibraryBar';
import AssetTagsForm from '../components/AssetTagsForm';
import { useIframeHandshake } from '../../hooks/useIframeHandshake';
import { ensurePlatformAccessTokenReady } from '../../../auth/platformSession';
import AssetInformationButton from './AssetInformationButton';
import { useAssetInformationSelection } from './useAssetInformationSelection';
const { useState, useEffect, useRef } = React;

declare var InovxLoader: any;

interface ViewerProps { }

const Viewer = React.forwardRef<unknown, ViewerProps>((props, ref) => {
    const outerSplitterInstance = useRef<SplitterComponent | null>(null);
    const propertySplitterInstance = useRef<SplitterComponent | null>(null);
    const viewerShellRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const viewerShell = viewerShellRef.current;
        if (!viewerShell) return;

        const preventNativeContextMenu = (event: MouseEvent) => {
            event.preventDefault();
        };

        viewerShell.addEventListener('contextmenu', preventNativeContextMenu, true);
        return () => viewerShell.removeEventListener('contextmenu', preventNativeContextMenu, true);
    }, []);

    const [selectedAsset, setSelectedAsset] = useState<string>(ViewerStore.getSelectedAsset());
    const [isAssetPanelVisible, setAssetPanelVisible] = useState<boolean>(ViewerStore.getAssetPanelVisibility());
    const isAssetSelected = useAssetInformationSelection();
    const showSplitter = isAssetPanelVisible && isAssetSelected;
    const [documentLink, setDocumentLink] = useState<string>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [renderMiniMap, setRenderMiniMap] = useState<boolean>(ViewerStore.isMiniMapOpen());
    const iframeHandshake = useIframeHandshake(documentLink, accessToken);

    const mountedRef = React.useRef(true);
    React.useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    React.useEffect(() => {
        const handleViewerStoreChange = () => {
            if (!mountedRef.current) return; // prevent setState after unmount
            addTooltipForTruncatedText();
            setSelectedAsset(ViewerStore.getSelectedAsset());
            setAssetPanelVisible(ViewerStore.getAssetPanelVisibility());
            setRenderMiniMap(current => current || ViewerStore.isMiniMapOpen());
        };

        ViewerStore.addChangeListener(handleViewerStoreChange);

        return () => {
            ViewerStore.removeChangeListener(handleViewerStoreChange);
        };
    }, []);

    useEffect(() => {
        let link: string = null;
        let cancelled = false;

        if (selectedAsset) {
            const asset = FacilityStore.getAssetById(selectedAsset);
            if (asset) {
                const nativeAsset = FacilityStore.getFacilityAssetsData().NativeAssets.find((a) => a.AssetID == selectedAsset && a.Source != AssetSourceType.VPlant);
                if (nativeAsset?.DocumentUrl) {
                    const baseUrl = new URL(nativeAsset.DocumentUrl);
                    link = `${baseUrl.origin}/embedded?callbackUrl=${encodeURIComponent(baseUrl.toString())}`;
                }
            }
        }

        setDocumentLink(link);

        if (!link) {
            setAccessToken(null);
            return () => {
                cancelled = true;
            };
        }

        void ensurePlatformAccessTokenReady()
            .then((token) => {
                if (!cancelled) {
                    setAccessToken(token);
                }
            })
            .catch((error) => {
                console.warn('Failed to prepare iframe access token', error);
                if (!cancelled) {
                    setAccessToken(null);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [selectedAsset]);

    useEffect(() => {
        // This is a hack - the pano viewer is not resizing properly when the splitter is collapsed/expanded.
        setTimeout(() => {
            handleResizing();
        }, 100);
    }, [showSplitter]);

    const addTooltipForTruncatedText = () => {
        setTimeout(() => {
            const elements = document.getElementsByClassName("single-line-ellipsis");
            for (let i = 0; i < elements.length; i++) {
                const div: any = elements[i];
                if (div) {
                    if (div.scrollWidth > div.clientWidth) {
                        div.title = div.textContent.trim();
                    } else {
                        div.removeAttribute('title');
                    }
                }
            }
        }, 10);
    };

    const handleResizing = () => {
        addTooltipForTruncatedText();
        window.dispatchEvent(new Event('resize'));
    };

    const closeAssetInformationPanel = React.useCallback(() => {
        ViewerActions.changeAssetPanelVisibility(false);
    }, []);
    const changePanelVisibility = React.useCallback(() => {
        ViewerActions.changeAssetPanelVisibility(!ViewerStore.getAssetPanelVisibility());
    }, []);

    let showPointCloudButton = false; // !!ViewerStore.getSelectedViewImage()?.ScanFileData;
    let showPointCloud = ViewerStore.isOpenPointCloud();
    const directionUpdateSource = ViewerStore.getDirectionUpdateSource();
    showPointCloud = directionUpdateSource == 1;

    // STABLE, typed resize handlers for EJ2 components
    const onOuterSplitResize = React.useCallback((e?: any) => {
        window.dispatchEvent(new Event('resize'));
    }, []);

    const onPropertySplitResize = React.useCallback((e?: any) => {
        // no-op but stable
    }, []);

    return (
        <div id="target" className="control-section splitter-expand" ref={viewerShellRef}>
            <div className="splitter-content-expand" id="PanoContainer">
                <MeasurementBar />
                <AssemblyLibraryBar />
                <AssetTagsBar mode="panoviewer" />
                <AssetTagsForm />
                <AssetInformationButton isExpanded={showSplitter} onShowAssetInformation={changePanelVisibility} />
                {showPointCloudButton ? <ShowPointCloudButton /> : <></>}
                {showPointCloud ? (
                    <ReactPointViewer displayMiniMap={renderMiniMap} />
                ) : (
                    <ReactPanoViewer displayMiniMap={renderMiniMap} />
                )}
            </div>

            <div id="PropertyPanel" className="h-100p" >
                <div id="AssetInfoContainer" className="h-100p" >
                    <AssetInformation isVisible={showSplitter} onClose={closeAssetInformationPanel} />
                </div>
                <div id="AssetDocContainer" className="h-100p" >
                    {showSplitter && documentLink && iframeHandshake?.status !== 'idle' && <iframe ref={iframeHandshake.iframeRef} src={documentLink} className="document-iframe" title={InovxLoader.t('facility:viewer.embeddedDocument')} />}
                </div>
                {/* Keep the property splitter mounted so reopening preserves React event handlers. */}
                <SplitterComponent
                    resizing={onPropertySplitResize}
                    separatorSize={4}
                    ref={propertySplitterInstance}
                    orientation="Vertical"
                >
                    <PanesDirective>
                        <PaneDirective collapsible={true} size={documentLink ? "60%" : "100%"} content="#AssetInfoContainer" />
                        <PaneDirective collapsible={true} content="#AssetDocContainer" />
                    </PanesDirective>
                </SplitterComponent>
            </div>

            <SplitterComponent
                resizing={onOuterSplitResize}
                height="100%"
                width="100%"
                separatorSize={4}
                ref={outerSplitterInstance as any}
            >
                <PanesDirective>
                    <PaneDirective size="75%" min="300px" content="#PanoContainer" />
                    <PaneDirective size="25%" min="300px" content="#PropertyPanel" collapsed={!showSplitter} />
                </PanesDirective>
            </SplitterComponent>

        </div >
    );
});

export default Viewer;
